///
/// EarthBrowser
///
/// Created by Matt Giger on 4/27/2026
/// Copyright (c) 2026 Geodata Labs
///

import { useIsMobile } from "@/components/utils/use-mobile"

function MouseIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 40 64" fill="none" className={className}>
			<rect x="2" y="2" width="36" height="60" rx="18" stroke="currentColor" strokeWidth="2.5" />
			<line x1="20" y1="2" x2="20" y2="26" stroke="currentColor" strokeWidth="1.5" />
			<rect x="15" y="8" width="10" height="14" rx="5" stroke="currentColor" strokeWidth="1.5" />
		</svg>
	)
}

function ArrowPair({ direction }: { direction: "horizontal" | "vertical" }) {
	const isH = direction === "horizontal"
	return (
		<svg viewBox="0 0 48 24" fill="none" className={`w-12 h-6 ${isH ? "" : "rotate-90"}`}>
			<path d="M4 12H44" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
			<path d="M10 6L4 12L10 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
			<path d="M38 6L44 12L38 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	)
}

function KeyBadge({ label, className }: { label: string, className?: string }) {
	return (
		<span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-md bg-slate-700 border border-slate-600 text-xs font-mono font-semibold text-slate-200 shadow-sm ${className ?? ""}`}>
			{label}
		</span>
	)
}

function DesktopDiagram() {
	return (
		<div className="w-full max-w-md space-y-4">
			{/* Shift+Drag explanation */}
			<div className="rounded-xl border border-slate-700 bg-background/50 p-4">
				<div className="flex items-center gap-2 mb-3">
					<KeyBadge label="Shift" />
					<span className="text-sm text-muted-foreground">+ drag to control tilt & bearing</span>
				</div>

				<div className="grid grid-cols-2 gap-3">
					{/* Vertical drag = tilt */}
					<div className="flex flex-col items-center gap-2 rounded-lg bg-slate-800/50 border border-slate-700/50 p-3">
						<div className="flex items-center gap-2 text-indigo-400">
							<MouseIcon className="w-6 h-10" />
							<ArrowPair direction="vertical" />
						</div>
						<div className="text-center">
							<p className="text-xs font-semibold text-foreground">Drag Up / Down</p>
							<p className="text-[11px] text-muted-foreground mt-0.5">Tilts the camera angle</p>
						</div>
					</div>

					{/* Horizontal drag = bearing */}
					<div className="flex flex-col items-center gap-2 rounded-lg bg-slate-800/50 border border-slate-700/50 p-3">
						<div className="flex items-center gap-2 text-blue-400">
							<MouseIcon className="w-6 h-10" />
							<ArrowPair direction="horizontal" />
						</div>
						<div className="text-center">
							<p className="text-xs font-semibold text-foreground">Drag Left / Right</p>
							<p className="text-[11px] text-muted-foreground mt-0.5">Rotates the bearing</p>
						</div>
					</div>
				</div>
			</div>

			{/* Quick reference */}
			<div className="rounded-xl border border-slate-700 bg-background/50 p-4 space-y-2">
				<p className="text-xs font-semibold text-foreground mb-2">Quick Reference</p>
				<div className="flex items-start gap-2 text-sm text-muted-foreground">
					<span className="text-indigo-400 mt-0.5">•</span>
					<span>Hold <KeyBadge label="Shift" /> then drag <strong className="text-foreground">vertically</strong> to tilt</span>
				</div>
				<div className="flex items-start gap-2 text-sm text-muted-foreground">
					<span className="text-blue-400 mt-0.5">•</span>
					<span>Hold <KeyBadge label="Shift" /> then drag <strong className="text-foreground">horizontally</strong> to rotate</span>
				</div>
				<div className="flex items-start gap-2 text-sm text-muted-foreground">
					<span className="text-cyan-400 mt-0.5">•</span>
					<span>Click the <strong className="text-foreground">compass</strong> to snap back to north-up</span>
				</div>
			</div>
		</div>
	)
}

function TouchFingers({ count, className }: { count: number, className?: string }) {
	return (
		<svg viewBox="0 0 48 48" fill="none" className={className}>
			{count >= 1 && (
				<g>
					<circle cx={count === 1 ? 24 : 18} cy="20" r="6" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.15" />
					<line x1={count === 1 ? 24 : 18} y1="26" x2={count === 1 ? 24 : 18} y2="40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
				</g>
			)}
			{count >= 2 && (
				<g>
					<circle cx="30" cy="20" r="6" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.15" />
					<line x1="30" y1="26" x2="30" y2="40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
				</g>
			)}
		</svg>
	)
}

function MobileDiagram() {
	return (
		<div className="w-full max-w-md space-y-3">
			{/* Single finger */}
			<div className="rounded-xl border border-slate-700 bg-background/50 p-4">
				<div className="flex items-center gap-3">
					<div className="text-indigo-400 flex-shrink-0">
						<TouchFingers count={1} className="w-10 h-10" />
					</div>
					<div>
						<p className="text-sm font-semibold text-foreground">One Finger Drag</p>
						<p className="text-xs text-muted-foreground mt-0.5">Pan the globe to explore different regions</p>
					</div>
				</div>
			</div>

			{/* Pinch */}
			<div className="rounded-xl border border-slate-700 bg-background/50 p-4">
				<div className="flex items-center gap-3">
					<div className="text-cyan-400 flex-shrink-0">
						<TouchFingers count={2} className="w-10 h-10" />
					</div>
					<div>
						<p className="text-sm font-semibold text-foreground">Pinch In / Out</p>
						<p className="text-xs text-muted-foreground mt-0.5">Zoom about your fingers; pinch, twist and drag all at once</p>
					</div>
				</div>
			</div>

			{/* Two-finger rotate */}
			<div className="rounded-xl border border-slate-700 bg-background/50 p-4">
				<div className="flex items-center gap-3">
					<div className="text-blue-400 flex-shrink-0">
						<TouchFingers count={2} className="w-10 h-10" />
					</div>
					<div>
						<p className="text-sm font-semibold text-foreground">Two-Finger Rotate</p>
						<p className="text-xs text-muted-foreground mt-0.5">Twist two fingers to rotate the camera bearing</p>
					</div>
				</div>
			</div>

			{/* Tilt control */}
			<div className="rounded-xl border border-slate-700 bg-background/50 p-4">
				<div className="flex items-center gap-3">
					<div className="text-emerald-400 flex-shrink-0">
						<svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
							<rect x="8" y="16" width="32" height="4" rx="2" stroke="currentColor" strokeWidth="2" />
							<circle cx="24" cy="18" r="6" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.15" />
							<path d="M24 10L24 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
							<path d="M24 30L24 26" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
							<path d="M21 7L24 4L27 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
							<path d="M21 29L24 32L27 29" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
					</div>
					<div>
						<p className="text-sm font-semibold text-foreground">Two-Finger Tilt</p>
						<p className="text-xs text-muted-foreground mt-0.5">Drag two fingers up or down together to angle the camera</p>
					</div>
				</div>
			</div>
		</div>
	)
}

export function CameraControlsDiagram() {
	const mobile = useIsMobile()

	return mobile ? <MobileDiagram /> : <DesktopDiagram />
}
